import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AlertHeader, Button, ButtonExportCSVAsync, LoaderSpin, Modal, ModalContent, ModalFooter, ModalHeader, ModalText, ModalTitle, Text, Wrapper } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getPaymentRequest, Payment, selectPayment } from 'store/Payment';
import { IStore } from 'store/types';
import { IPaymentSet, ITipCalculatorParticipant, ITipCalculatorPayee } from 'types';
import { centsToDollarsDisplay, formatDateForUILongFormat, getPaymentAdjustments } from 'utils';
import { CSVFormatter } from 'utils/csvformatter';

interface IPaymentAdjustmentsModalProps {
  originalPayment: IPaymentSet;
  currentPayees: ITipCalculatorPayee[];
  currentParticipants: ITipCalculatorParticipant[];
  hideModal?: () => void;
}

const Body = styled(Wrapper)`
  .loader {
    margin: ${toRem(theme.space['2xl'])};
  }
  .business-date {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    margin-top: ${toRem(theme.space.lg)};
  }
`;

const Header = styled.p`
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const ItemsSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-items: center;
  border: 1px solid ${theme.color.neutral[200]};
  width: 100%;
  overflow-x: auto;
  margin-top: ${toRem(theme.space.lg)};
  padding: ${toRem(theme.space.xl)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};

  @media (max-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(theme.space.lg)};
  }
`;

const HorizontalScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${toRem(460)};
  gap: ${toRem(theme.space.sm)};
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr 1.2fr 1fr 1fr 1.1fr;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.lg)};

  & > *:not(:first-child) {
    text-align: right;
  }

  @media (max-width: ${theme.breakpoint.med}px) {
    gap: ${toRem(theme.space.sm)} ${toRem(theme.space.xs)};
  }
`;

const mainText = 'The table below shows the differences between the original and recalculated payments. Export this table as a file to review or manage adjustments.';
const getEmptyText = (paymentDate: string) => `There are no differences between the original and recalculated payments for ${formatDateForUILongFormat(paymentDate)}.`;

const getFilename = ({ site, paymentDate }: IPaymentSet) => `Adjustments_${site.name}_${paymentDate}`;

const getEmptyModal = (payment: IPaymentSet, onClose?: () => void) => (
  <>
    <AlertHeader title="No Payment Adjustments Found" />
    <ModalContent>
      <ModalText>{getEmptyText(payment.paymentDate)}</ModalText>
    </ModalContent>
    <ModalFooter columns={1}>
      <Button label="Close" size="large" appearance="white" onClick={onClose} data-cy="cancel-button" />
    </ModalFooter>
  </>
);

const PaymentAdjustmentsModal: React.FC<IPaymentAdjustmentsModalProps> = ({ originalPayment, currentPayees, currentParticipants, hideModal }) => {
  const dispatch = useDispatch();
  const storedPayment = useSelector(selectPayment());
  const isFetching = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.GET_PAYMENT_REQUEST));
  const paymentWithPayouts = storedPayment?.id === originalPayment.id && storedPayment.calculations ? storedPayment : undefined;
  const isLoading = isFetching || !paymentWithPayouts;

  useEffect(() => {
    if (!paymentWithPayouts) dispatch(getPaymentRequest(originalPayment.id, false, undefined, hideModal));
  }, [dispatch, hideModal, originalPayment.id]);

  const adjustments = useMemo(() => {
    if (isLoading) return [];
    return getPaymentAdjustments(paymentWithPayouts, currentPayees, currentParticipants);
  }, [isLoading, paymentWithPayouts, currentPayees, currentParticipants]);

  const getExportCSV = async () => CSVFormatter.paymentAdjustments(adjustments);

  const isEmpty = !adjustments.length;

  return (
    <Modal maxWidth={isEmpty ? 580 : 680} isActive onClose={hideModal} data-cy="payment-adjustments-modal">
      {isLoading ? (
        <Body direction="column" alignItems="center" width="100%">
          <LoaderSpin size={60} stroke={5} color={theme.color.secondary[400]} className="loader" />
        </Body>
      ) : isEmpty ? (
        getEmptyModal(originalPayment, hideModal)
      ) : (
        <>
          <ModalHeader>
            <ModalTitle>Payment Adjustments</ModalTitle>
          </ModalHeader>
          <Body direction="column" alignItems="center" width="100%">
            <ModalText>{mainText}</ModalText>
            <Text fontWeight="semibold" className="business-date">
              {formatDateForUILongFormat(originalPayment.paymentDate)}
            </Text>
            <ItemsSection>
              <HorizontalScrollContainer>
                <ItemRow data-cy="adjustment-header-row">
                  <Header>Employee</Header>
                  <Header>Original</Header>
                  <Header>Recalculated</Header>
                  <Header>Difference</Header>
                  <Header>Tips</Header>
                  <Header>Autograts</Header>
                </ItemRow>
                {adjustments.map((entry, idx) => (
                  <ItemRow key={`entry-${idx}`} data-cy="adjustment-row">
                    <Text size="xsmall" fontWeight="medium" allowWordWrap>
                      {entry.firstName} {entry.lastName}
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      {centsToDollarsDisplay(entry.original)}
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      {centsToDollarsDisplay(entry.recalculated)}
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      {centsToDollarsDisplay(entry.difference)}
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      {centsToDollarsDisplay(entry.tips)}
                    </Text>
                    <Text size="xsmall" fontWeight="medium">
                      {centsToDollarsDisplay(entry.autograts)}
                    </Text>
                  </ItemRow>
                ))}
              </HorizontalScrollContainer>
            </ItemsSection>
          </Body>
          <ModalFooter columns={2}>
            <Button label="Cancel" appearance="white" onClick={hideModal} data-cy="cancel-button" />
            <ButtonExportCSVAsync label="Download File" size="large" exportFunc={getExportCSV} fileName={getFilename(originalPayment)} data-cy="download-button" />
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};
export default PaymentAdjustmentsModal;
